/**
 * Doc-Completeness Check — ENT-65 / FEAT-201
 * BET-99: Complements the code-based coherence engine with DB-sourced field checks.
 *
 * Checks that collection documents have the required fields populated after the
 * DB-first migration. These checks replace the removed cross-registry sync rules
 * (sync-classification-defaults, sync-classification-classifier) which produced
 * false positives once CLASSIFICATION_MAP and CLASSIFICATION_META became seed-only.
 *
 * Rules:
 *   - All collections: idPrefix, governed must be set
 *   - Classifier collections (classificationCheck present): classificationPriority required
 *   - Workflow collections (validWorkflowStatuses present): array must be non-empty
 */

export interface CollectionDocFields {
  slug: string;
  governed?: boolean | null;
  navGroup?: string | null;
  idPrefix?: string | null;
  validWorkflowStatuses?: string[] | null;
  classificationCheck?: string | null;
  classificationPriority?: number | null;
}

export interface DocCompletenessViolation {
  slug: string;
  field: string;
  message: string;
  severity: "error" | "warning";
  fix: string;
}

export interface DocCompletenessReport {
  checkedAt: number;
  totalChecked: number;
  violations: DocCompletenessViolation[];
  summary: { errors: number; warnings: number; total: number };
}

/**
 * Check collection docs for required field completeness.
 * Pure function — accepts pre-fetched collection data, no I/O.
 */
export function checkDocCompleteness(
  collections: CollectionDocFields[],
  checkedAt?: number,
): DocCompletenessReport {
  const violations: DocCompletenessViolation[] = [];

  for (const col of collections) {
    const { slug } = col;

    // governed must be set (boolean)
    if (col.governed === undefined || col.governed === null) {
      violations.push({
        slug,
        field: "governed",
        message: `"${slug}" is missing the governed flag`,
        severity: "warning",
        fix: `Set governed on the "${slug}" collection document`,
      });
    }

    // idPrefix must be set
    if (!col.idPrefix) {
      violations.push({
        slug,
        field: "idPrefix",
        message: `"${slug}" is missing idPrefix`,
        severity: "warning",
        fix: `Set idPrefix on the "${slug}" collection document`,
      });
    }

    // Classifier fields must be consistent: check ↔ priority together
    const hasCheck = typeof col.classificationCheck === "string" && col.classificationCheck.length >= 3;
    const hasPriority = col.classificationPriority != null;

    if (hasCheck && !hasPriority) {
      violations.push({
        slug,
        field: "classificationPriority",
        message: `"${slug}" has classificationCheck but missing classificationPriority (1–9)`,
        severity: "error",
        fix: `Set classificationPriority (1–9) on the "${slug}" collection document`,
      });
    }

    if (hasPriority && !hasCheck) {
      violations.push({
        slug,
        field: "classificationCheck",
        message: `"${slug}" has classificationPriority but missing classificationCheck`,
        severity: "error",
        fix: `Set classificationCheck (3–500 chars) on the "${slug}" collection document`,
      });
    }

    // validWorkflowStatuses must be non-empty when present
    if (Array.isArray(col.validWorkflowStatuses) && col.validWorkflowStatuses.length === 0) {
      violations.push({
        slug,
        field: "validWorkflowStatuses",
        message: `"${slug}" has an empty validWorkflowStatuses array`,
        severity: "error",
        fix: `Add at least one status to validWorkflowStatuses on "${slug}", or remove the field`,
      });
    }
  }

  const errors = violations.filter((v) => v.severity === "error").length;
  const warnings = violations.filter((v) => v.severity === "warning").length;

  return {
    checkedAt: checkedAt ?? Date.now(),
    totalChecked: collections.length,
    violations,
    summary: { errors, warnings, total: violations.length },
  };
}

/**
 * Render doc-completeness report as orient section lines.
 * Returns empty array when there are no violations (clean state).
 */
export function renderDocCompletenessLines(report: DocCompletenessReport): string[] {
  // TEN-2036: clearer label than "Classification Doc-Completeness" — names what the report counts.
  const lines: string[] = ["## Collection Documentation Gaps"];

  if (report.summary.total === 0) {
    lines.push(`All ${report.totalChecked} collection docs have required fields populated.`);
    lines.push("");
    return lines;
  }

  const parts: string[] = [];
  if (report.summary.errors > 0) parts.push(`${report.summary.errors} error${report.summary.errors === 1 ? "" : "s"}`);
  if (report.summary.warnings > 0) parts.push(`${report.summary.warnings} warning${report.summary.warnings === 1 ? "" : "s"}`);
  lines.push(`**${report.summary.total} gap${report.summary.total === 1 ? "" : "s"}** across ${report.totalChecked} collections (${parts.join(", ")})`);

  const errors = report.violations.filter((v) => v.severity === "error");
  const warnings = report.violations.filter((v) => v.severity === "warning");

  // TEN-2035: bumped truncation caps from 5/3 to 10 each. Earlier limit hid 24+
  // warnings behind "...and N more" — high enough that triage was blocked. 10
  // covers most workspaces in full and keeps the worst case to a single
  // truncation line.
  const ERROR_INLINE = 10;
  const WARNING_INLINE = 10;

  if (errors.length > 0) {
    lines.push("");
    lines.push("**Errors** (must fix):");
    for (const v of errors.slice(0, ERROR_INLINE)) {
      lines.push(`- ${v.message} — ${v.fix}`);
    }
    if (errors.length > ERROR_INLINE) lines.push(`- ...and ${errors.length - ERROR_INLINE} more`);
  }

  if (warnings.length > 0) {
    lines.push("");
    lines.push("**Warnings**:");
    for (const v of warnings.slice(0, WARNING_INLINE)) {
      lines.push(`- ${v.message}`);
    }
    if (warnings.length > WARNING_INLINE) lines.push(`- ...and ${warnings.length - WARNING_INLINE} more`);
  }

  lines.push("");
  return lines;
}
